import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

/**
 * Five variants, in descending order of how much attention they claim. A screen
 * gets one `primary`; everything else is secondary or quieter. That constraint
 * is what makes the primary action findable without making it loud.
 *
 * Solid fills carry a hairline top highlight so they read as a raised surface
 * rather than a flat rectangle, and shed it on press.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-contrast shadow-sm hover:bg-accent-hover active:bg-accent-active ' +
    'inset-shadow-[0_1px_0_rgb(255_255_255/0.2)] ' +
    'active:shadow-none active:inset-shadow-none disabled:hover:bg-accent',
  secondary:
    'border border-line bg-surface-raised text-ink shadow-xs hover:bg-surface-hover ' +
    'hover:border-line-strong active:bg-surface-active active:shadow-none',
  subtle:
    'bg-surface-sunken text-ink-secondary hover:bg-surface-hover hover:text-ink ' +
    'active:bg-surface-active',
  ghost:
    'text-ink-secondary hover:bg-surface-hover hover:text-ink active:bg-surface-active',
  danger:
    'bg-critical text-white shadow-sm hover:brightness-110 active:brightness-95 ' +
    'inset-shadow-[0_1px_0_rgb(255_255_255/0.2)] active:shadow-none active:inset-shadow-none',
}

/** Heights track the type scale: text grew, so the boxes around it did too. */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-sm',
  md: 'h-9 gap-2 px-3.5 text-base',
  lg: 'h-11 gap-2 px-5 text-base',
}

/**
 * Square sizes for an icon-only button. A separate map, *replacing* SIZES
 * rather than being appended after it.
 *
 * Appending `px-0` looked like it worked and did not: Tailwind orders
 * utilities by its own sort in the generated stylesheet, not by their order in
 * the class attribute, so `px-3.5` kept winning. Every IconButton in the app
 * was therefore 36px wide with 28px of padding, leaving 8px for the glyph —
 * an 18px icon rendered 6px wide, which is why the theme switch and the dialog
 * close buttons read as empty boxes.
 */
const SQUARE_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 px-0',
  md: 'h-9 w-9 px-0',
  lg: 'h-11 w-11 px-0',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Leading glyph. Kept out of `children` so the gap is consistent. */
  icon?: ReactNode
  /** Fills the row it sits in, for stacked mobile actions. */
  block?: boolean
  /** Square, no horizontal padding. Set by `IconButton`; see SQUARE_SIZES. */
  square?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading,
    icon,
    block,
    square,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md font-medium whitespace-nowrap',
        // Icons are flex children with an intrinsic width; without this they
        // are free to shrink and render as slivers inside a tight box.
        '[&_svg]:shrink-0',
        // The 1px drop on press is the only motion; the global reduced-motion
        // rule neutralises the transition, not the offset.
        'transition-[background-color,border-color,box-shadow,translate,filter] duration-100 ease-out',
        'active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
        block && 'w-full',
        VARIANTS[variant],
        square ? SQUARE_SIZES[size] : SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true">
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="28"
            strokeDashoffset="20"
          />
        </svg>
      ) : (
        icon
      )}
      {children}
    </button>
  )
})

export interface IconButtonProps
  extends Omit<ButtonProps, 'children' | 'block' | 'square'> {
  /** Required: an icon alone has no accessible name. */
  label: string
  children: ReactNode
}

/**
 * A square button holding one glyph.
 *
 * Existed as four near-identical hand-rolled copies (theme toggle, two dialog
 * close buttons, the toast dismiss), each with slightly different padding and
 * one missing its accessible name.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ label, size = 'md', className, children, ...props }, ref) {
    return (
      <Button
        ref={ref}
        size={size}
        square
        aria-label={label}
        title={props.title ?? label}
        className={className}
        {...props}
      >
        {children}
      </Button>
    )
  },
)
